import axios from 'axios';

// Use environment variable or default to HTTPS backend
const API_URL = process.env.REACT_APP_API_URL || 'https://api.spliteasy.aimiq.co.za/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Token auth is handled via Authorization header; avoid credentialed CORS.
  withCredentials: false,
});

// Add token to requests if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle response errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Log CORS errors for debugging
    if (error.code === 'ERR_NETWORK' || error.message?.includes('CORS') || error.message?.includes('Network Error')) {
      console.error('CORS Error Details:', {
        message: error.message,
        code: error.code,
        config: {
          url: error.config?.url,
          baseURL: error.config?.baseURL,
          method: error.config?.method,
          withCredentials: error.config?.withCredentials,
          headers: error.config?.headers,
        },
      });
    }

    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data: { email: string; password: string; name: string }) =>
    api.post('/auth/register', data),
  
  login: (data: { email: string; password: string }) =>
    api.post('/auth/login', data),
  
  getMe: () =>
    api.get('/auth/me'),
};

// Split Sheets API
export const splitSheetsAPI = {
  create: (data: {
    songTitle: string;
    songDetails?: string;
    // South African enhanced fields
    workType?: string;
    copyrightOwner?: string;
    publishingRights?: string;
    recordLabel?: string;
    isrcCode?: string;
    iswcCode?: string;
    releaseDate?: string;
    recordingDate?: string;
    recordingLocation?: string;
    governingLaw?: string;
    jurisdiction?: string;
    agreementDate?: string;
    effectiveDate?: string;
    expiryDate?: string;
    // Additional SA fields
    alternateTitles?: string;
    dateOfCreation?: string;
    sessionDate?: string;
    albumName?: string;
    projectName?: string;
    genre?: string;
    studioName?: string;
    studioLocation?: string;
    recordingEngineer?: string;
    mixer?: string;
    instrumentsUsed?: string;
    samplesUsed?: string;
    sampleClearances?: string;
    publisherName?: string;
    adminName?: string;
    publisherContact?: string;
    samroMembership?: string;
    capassoMembership?: string;
    sampraMembership?: string;
    impraMembership?: string;
    collaborators: Array<{
      name: string;
      stage_name?: string;
      email: string;
      phone?: string;
      role: string;
      contribution_type?: string;
      contribution_description?: string;
      percentage: number;
      // SA Legal fields
      id_number?: string;
      passport_number?: string;
      nationality?: string;
      address_line1?: string;
      address_line2?: string;
      city?: string;
      province?: string;
      postal_code?: string;
      country?: string;
      date_of_birth?: string;
      publisher_name?: string;
      publisher_contact?: string;
      samro_membership?: string;
      samro_account_number?: string;
      capasso_membership?: string;
      capasso_account_number?: string;
      sampra_membership?: string;
      sampra_account_number?: string;
      impra_membership?: string;
      impra_account_number?: string;
      writer_share?: number;
      producer_share?: number;
      performer_share?: number;
      is_minor?: boolean;
      guardian_name?: string;
      guardian_id_number?: string;
      guardian_relationship?: string;
      guardian_contact?: string;
    }>;
  }) => api.post('/split-sheets', data),

  getAll: () => api.get('/split-sheets'),

  getOne: (id: string) => api.get(`/split-sheets/${id}`),

  update: (id: string, data: any) => api.put(`/split-sheets/${id}`, data),

  delete: (id: string) => api.delete(`/split-sheets/${id}`),

  sign: (id: string, data: { signatureData: string; collaboratorId: number }) =>
    api.post(`/split-sheets/${id}/sign`, data),

  getPDF: (id: string) => api.get(`/split-sheets/${id}/pdf`),

  getCollaborators: (id: string) => api.get(`/split-sheets/${id}/collaborators`),

  // South African enhanced split sheet creation
  createSouthAfrican: (data: {
    songTitle: string;
    songDetails?: string;
    workType?: string;
    copyrightOwner?: string;
    publishingRights?: string;
    recordLabel?: string;
    isrcCode?: string;
    iswcCode?: string;
    releaseDate?: string;
    recordingDate?: string;
    recordingLocation?: string;
    governingLaw?: string;
    jurisdiction?: string;
    agreementDate?: string;
    effectiveDate?: string;
    expiryDate?: string;
    // Additional SA fields
    alternateTitles?: string;
    dateOfCreation?: string;
    sessionDate?: string;
    albumName?: string;
    projectName?: string;
    genre?: string;
    studioName?: string;
    studioLocation?: string;
    recordingEngineer?: string;
    mixer?: string;
    instrumentsUsed?: string;
    samplesUsed?: string;
    sampleClearances?: string;
    publisherName?: string;
    adminName?: string;
    publisherContact?: string;
    samroMembership?: string;
    capassoMembership?: string;
    sampraMembership?: string;
    impraMembership?: string;
    collaborators: Array<{
      name: string;
      stage_name?: string;
      email: string;
      phone?: string;
      role: string;
      contribution_type?: string;
      contribution_description?: string;
      percentage: number;
      id_number?: string;
      passport_number?: string;
      nationality?: string;
      address_line1?: string;
      address_line2?: string;
      city?: string;
      province?: string;
      postal_code?: string;
      country?: string;
      date_of_birth?: string;
      publisher_name?: string;
      publisher_contact?: string;
      samro_membership?: string;
      samro_account_number?: string;
      capasso_membership?: string;
      capasso_account_number?: string;
      sampra_membership?: string;
      sampra_account_number?: string;
      impra_membership?: string;
      impra_account_number?: string;
      writer_share?: number;
      producer_share?: number;
      performer_share?: number;
      is_minor?: boolean;
      guardian_name?: string;
      guardian_id_number?: string;
      guardian_relationship?: string;
      guardian_contact?: string;
    }>;
  }) => api.post('/split-sheets-sa', data),
};

export default api;

